/**
 * auth.ts — WebAuthn passkey ceremony helpers and JWT storage.
 *
 * Manages player registration and login via the browser's built-in WebAuthn
 * API (navigator.credentials.create / navigator.credentials.get).
 * The resulting JWT is stored in sessionStorage under AUTH_TOKEN_KEY.
 *
 * Usage from other modules:
 *   import { isAuthenticated, getToken, register, login } from './auth';
 */

import { API_BASE } from './api';

const AUTH_TOKEN_KEY = 'ff_auth_token';
const AUTH_USERNAME_KEY = 'ff_username';

// ── Token helpers ─────────────────────────────────────────────────────────────

/** Returns true if a non-expired JWT is present in sessionStorage. */
export function isAuthenticated(): boolean {
  const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
  if (!token) return false;
  try {
    const payload = parseJwtPayload(token);
    const now = Math.floor(Date.now() / 1000);
    return payload.exp > now;
  } catch {
    return false;
  }
}

/** Returns the stored JWT token, or null if not authenticated. */
export function getToken(): string | null {
  if (!isAuthenticated()) return null;
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

/** Returns the stored username, or null if not registered. */
export function getUsername(): string | null {
  return localStorage.getItem(AUTH_USERNAME_KEY);
}

/** Clears the JWT from sessionStorage, ending the current session. username is preserved. */
export function logout(): void {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
}

function storeToken(token: string): void {
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
  // Cache the username from the JWT payload in localStorage for quick access.
  try {
    const payload = parseJwtPayload(token) as { exp: number; username?: string };
    if (payload.username) {
      localStorage.setItem(AUTH_USERNAME_KEY, payload.username);
    }
  } catch {
    // ignore parse errors — username display will just be empty
  }
}

function storeUsername(username: string): void {
  localStorage.setItem(AUTH_USERNAME_KEY, username);
}

// ── Registration ceremony ─────────────────────────────────────────────────────

/**
 * Runs the full WebAuthn registration ceremony.
 * On success the username is persisted in localStorage and the returned JWT is
 * stored in sessionStorage.
 */
export async function register(username: string, email: string, marketingConsent: boolean): Promise<void> {
  const startRes = await fetch(`${API_BASE}/v1/auth/register/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, email, marketingConsent }),
  });

  if (!startRes.ok) {
    const err = await startRes.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Registration start failed');
  }

  const { challengeId, challenge } = await startRes.json();

  const credential = await navigator.credentials.create({
    publicKey: decodeCreationOptions(challenge),
  });

  if (!credential) {
    throw new Error('Passkey creation was cancelled or failed');
  }

  const finishRes = await fetch(`${API_BASE}/v1/auth/register/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challengeId,
      credential: encodeCredential(credential as PublicKeyCredential),
    }),
  });

  if (!finishRes.ok) {
    const err = await finishRes.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Registration finish failed');
  }

  storeUsername(username);
  await login(username);
}

// ── Login ceremony ────────────────────────────────────────────────────────────

/**
 * Runs the full WebAuthn authentication ceremony for the given username.
 * On success the JWT is stored in sessionStorage.
 */
export async function login(username: string): Promise<void> {
  const startRes = await fetch(`${API_BASE}/v1/auth/login/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });

  if (!startRes.ok) {
    const err = await startRes.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Login start failed');
  }

  const { challengeId, challenge } = await startRes.json();

  const credential = await navigator.credentials.get({
    publicKey: decodeRequestOptions(challenge),
  });

  if (!credential) {
    throw new Error('Passkey assertion was cancelled or failed');
  }

  const finishRes = await fetch(`${API_BASE}/v1/auth/login/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      challengeId,
      credential: encodeCredential(credential as PublicKeyCredential),
    }),
  });

  if (!finishRes.ok) {
    const err = await finishRes.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? 'Login failed');
  }

  const { token, username: confirmedUsername } = await finishRes.json();
  storeToken(token);
  storeUsername(confirmedUsername);
}

// ── WebAuthn encoding helpers ─────────────────────────────────────────────────

/**
 * Decodes a CreationChallengeResponse from the server, converting base64url
 * strings to ArrayBuffers where the browser WebAuthn API expects them.
 */
function decodeCreationOptions(
  serverOptions: Record<string, unknown>
): PublicKeyCredentialCreationOptions {
  const opts = serverOptions as {
    publicKey?: {
      challenge?: string;
      user?: { id?: string; name?: string; displayName?: string };
      excludeCredentials?: Array<{ id?: string; type?: string }>;
    };
  };

  const inner = opts.publicKey ?? (opts as unknown as typeof opts.publicKey);

  return {
    ...(inner as object),
    challenge: base64urlToBuffer((inner as { challenge: string }).challenge),
    user: {
      ...(inner as { user: object }).user,
      id: base64urlToBuffer(
        ((inner as { user: { id: string } }).user?.id) ?? ''
      ),
    },
    excludeCredentials: (
      (inner as { excludeCredentials?: Array<{ id: string; type: string }> })
        .excludeCredentials ?? []
    ).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  } as unknown as PublicKeyCredentialCreationOptions;
}

/**
 * Decodes a RequestChallengeResponse from the server, converting base64url
 * strings to ArrayBuffers where the browser WebAuthn API expects them.
 */
function decodeRequestOptions(
  serverOptions: Record<string, unknown>
): PublicKeyCredentialRequestOptions {
  const opts = serverOptions as {
    publicKey?: {
      challenge?: string;
      allowCredentials?: Array<{ id?: string; type?: string }>;
    };
  };

  const inner = opts.publicKey ?? (opts as unknown as typeof opts.publicKey);

  return {
    ...(inner as object),
    challenge: base64urlToBuffer((inner as { challenge: string }).challenge),
    allowCredentials: (
      (inner as { allowCredentials?: Array<{ id: string; type: string }> })
        .allowCredentials ?? []
    ).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  } as unknown as PublicKeyCredentialRequestOptions;
}

/**
 * Encodes a PublicKeyCredential to a plain JSON-serialisable object by
 * converting all ArrayBuffers to base64url strings.
 */
function encodeCredential(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response;
  const result: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
  };

  if (response instanceof AuthenticatorAttestationResponse) {
    result.response = {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
    };
  } else if (response instanceof AuthenticatorAssertionResponse) {
    result.response = {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null,
    };
  }

  return result;
}

// ── Base64url utilities ───────────────────────────────────────────────────────

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// ── JWT parsing ───────────────────────────────────────────────────────────────

function parseJwtPayload(token: string): { exp: number } {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT format');
  const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
  return payload as { exp: number };
}
